import axios from "axios";
import {
  Conversation,
  Message,
  Participant,
  Media,
  Client,
  Paginator,
  User,
} from "@twilio/conversations";

import {
  MessageStatus,
  ReduxMessage,
} from "./store/reducers/messageListReducer";
import {
  CONVERSATION_MESSAGES,
  CONVERSATION_PAGE_SIZE,
  PARTICIPANT_MESSAGES,
  USER_PROFILE_MESSAGES,
} from "./constants";
import { NotificationsType } from "./store/reducers/notificationsReducer";
import { successNotification, unexpectedErrorNotification } from "./helpers";
import { getSdkMessageObject } from "./conversations-objects";
import { ReduxParticipant } from "./store/reducers/participantsReducer";

type ParticipantResponse = ReturnType<typeof Conversation.prototype.add>;

export async function addConversation(
  name: string,
  updateParticipants: (participants: Participant[], sid: string) => void,
  client?: Client,
  addNotifications?: (notifications: NotificationsType) => void
): Promise<Conversation> {
  if (client === undefined) {
    throw new Error(
      "Client is suddenly undefined, are you sure everything is ok?"
    );
  }

  if (name.length === 0) {
    throw new Error("Conversation name is empty");
  }

  try {
    const conversation = await client.createConversation({
      friendlyName: name,
    });
    await conversation.join();

    const participants = await conversation.getParticipants();
    updateParticipants(participants, conversation.sid);

    successNotification({
      message: CONVERSATION_MESSAGES.CREATED,
      addNotifications,
    });

    return conversation;
  } catch (e) {
    unexpectedErrorNotification(e.message, addNotifications);
    throw e;
  }
}

// Starts a text thread with a phone number: a conversation named after the
// number, with that number added as an SMS participant sent from the
// signed-in Twilio number
export async function startSmsConversation(
  recipient: string,
  updateParticipants: (participants: Participant[], sid: string) => void,
  client?: Client
): Promise<Conversation> {
  if (client === undefined) {
    throw new Error("Not connected yet. Please try again in a moment.");
  }

  const twilioNumber = (localStorage.getItem("username") ?? "").replace(
    /\D/g,
    ""
  );
  if (!/^1?\d{10}$/.test(twilioNumber)) {
    throw new Error("Sign in with a Twilio number to send texts.");
  }

  const conversation = await client.createConversation({
    friendlyName: recipient,
  });
  try {
    await conversation.join();
    await conversation.addNonChatParticipant(
      `+${twilioNumber.length === 10 ? "1" : ""}${twilioNumber}`,
      recipient,
      { friendlyName: recipient }
    );
  } catch (e) {
    // Don't leave an empty conversation behind
    await conversation.delete().catch(() => undefined);
    throw new Error(
      (e as Error).message.includes("already exists")
        ? "You already have a conversation with this number."
        : `Couldn't start a conversation with this number: ${
            (e as Error).message
          }`
    );
  }

  const participants = await conversation.getParticipants();
  updateParticipants(participants, conversation.sid);
  return conversation;
}

export async function addChatParticipant(
  name: string,
  convo?: Conversation,
  addNotifications?: (notifications: NotificationsType) => void
): Promise<ParticipantResponse> {
  if (convo === undefined) {
    throw new Error(
      "Conversation is suddenly undefined, are you sure everything is ok?"
    );
  }

  if (name.length === 0) {
    throw new Error("Participant name is empty");
  }

  try {
    const result = await convo.add(name);
    successNotification({
      message: PARTICIPANT_MESSAGES.ADDED,
      addNotifications,
    });
    return result;
  } catch (e) {
    unexpectedErrorNotification(e.message, addNotifications);
    throw e;
  }
}

export async function addNonChatParticipant(
  number: string,
  proxyNumber: string,
  convo?: Conversation,
  addNotifications?: (notifications: NotificationsType) => void
): Promise<ParticipantResponse> {
  if (convo === undefined) {
    throw new Error(
      "Conversation is suddenly undefined, are you sure everything is ok?"
    );
  }

  if (number.length === 0 || proxyNumber.length === 0) {
    throw new Error(
      "Both participant number and proxy number must be specified"
    );
  }

  try {
    const result = await convo.addNonChatParticipant(proxyNumber, number, {
      friendlyName: number,
    });
    successNotification({
      message: PARTICIPANT_MESSAGES.ADDED,
      addNotifications,
    });

    return result;
  } catch (e) {
    unexpectedErrorNotification(e.message, addNotifications);
    throw e;
  }
}

export async function readUserProfile(
  identity: string,
  client?: Client
): Promise<User | undefined> {
  try {
    return await client?.getUser(identity);
  } catch (e) {
    unexpectedErrorNotification(e.message);
    throw e;
  }
}

export async function updateFriendlyName(
  friendlyName: string,
  user?: User
): Promise<User | undefined> {
  try {
    const result = await user?.updateFriendlyName(friendlyName);
    successNotification({
      message: USER_PROFILE_MESSAGES.FRIENDLY_NAME_UPDATED,
    });

    return result;
  } catch (e) {
    unexpectedErrorNotification(e.message);
    throw e;
  }
}

export type LoginSession = {
  // Who this device chats as
  identity: string;
  token: string;
  session: string;
  // The login the session belongs to; differs from identity when an admin
  // is texting as another number
  owner?: string;
  // Every login an admin can switch to
  logins?: string[];
};

// The SMS login function lives next to /token-service in the same Twilio
// Functions service, so its address is derived from the token service URL
// unless REACT_APP_SMS_LOGIN_URL is set.
function getSmsLoginUrl(): string {
  const explicitUrl = process.env.REACT_APP_SMS_LOGIN_URL as string;
  if (explicitUrl) {
    return explicitUrl;
  }
  const tokenServiceUrl = process.env
    .REACT_APP_ACCESS_TOKEN_SERVICE_URL as string;
  if (!tokenServiceUrl) {
    throw new Error(
      "REACT_APP_ACCESS_TOKEN_SERVICE_URL is not configured, cannot login"
    );
  }
  return `${new URL(tokenServiceUrl).origin}/sms-login`;
}

async function callSmsLogin<T>(params: Record<string, string>): Promise<T> {
  const url = getSmsLoginUrl();
  try {
    const response = await axios.post(url, new URLSearchParams(params));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error("Couldn't reach the login service. Please try again.");
  }
}

// Texts a code to the mobile paired with this login; returns the masked mobile
export async function sendLoginCode(login: string): Promise<string> {
  const result = await callSmsLogin<{ to?: string }>({ action: "send", login });
  return result.to ?? "";
}

export async function verifyLoginCode(
  login: string,
  code: string
): Promise<LoginSession> {
  return callSmsLogin<LoginSession>({ action: "verify", login, code });
}

// Renews the saved login; an admin keeps texting as the number they picked
// unless "as" names another one
export async function refreshLogin(as?: string): Promise<LoginSession> {
  const username = localStorage.getItem("username") ?? "";
  const owner = localStorage.getItem("sessionOwner") || username;
  return callSmsLogin<LoginSession>({
    action: "refresh",
    identity: owner,
    session: localStorage.getItem("session") ?? "",
    as: as ?? username,
  });
}

export function saveLogin(login: LoginSession): void {
  localStorage.setItem("username", login.identity);
  localStorage.setItem("session", login.session);
  localStorage.setItem("sessionOwner", login.owner ?? login.identity);
  if (login.logins?.length) {
    localStorage.setItem("logins", JSON.stringify(login.logins));
  } else {
    localStorage.removeItem("logins");
  }
  // Passwords are no longer used; drop any saved by older versions
  localStorage.removeItem("password");
}

// The numbers an admin can switch between, or [] for everyone else
export function getSwitchableLogins(): string[] {
  try {
    return JSON.parse(localStorage.getItem("logins") ?? "[]");
  } catch (e) {
    return [];
  }
}

export function clearLogin(): void {
  ["username", "session", "sessionOwner", "logins", "password"].forEach((key) =>
    localStorage.removeItem(key)
  );
}

export async function getMessageStatus(
  message: ReduxMessage,
  channelParticipants: ReduxParticipant[]
): Promise<{
  [MessageStatus.Delivered]?: number;
  [MessageStatus.Read]?: number;
  [MessageStatus.Failed]?: number;
  [MessageStatus.Sending]?: number;
}> {
  // FIXME should be: return statuses[message.sid];
  // after this modification:
  // message.on("updated", ({ message, updateReasons }) => {
  // if reason includes "deliveryReceipt" {
  //   // paginate detailed receipts
  //   const receipts = await message.getDetailedDeliveryReceipts(); // paginated backend query every time
  // }
  // });

  const statuses = {
    [MessageStatus.Delivered]: 0,
    [MessageStatus.Read]: 0,
    [MessageStatus.Failed]: 0,
    [MessageStatus.Sending]: 0,
  };

  if (message.index === -1) {
    return Promise.resolve({
      ...statuses,
      [MessageStatus.Sending]: 1,
    });
  }

  channelParticipants.forEach((participant) => {
    if (
      participant.identity == localStorage.getItem("username") ||
      participant.type !== "chat"
    ) {
      return;
    }

    if (
      participant.lastReadMessageIndex &&
      participant.lastReadMessageIndex >= message.index
    ) {
      statuses[MessageStatus.Read] += 1;
    } else if (participant.lastReadMessageIndex !== -1) {
      statuses[MessageStatus.Delivered] += 1;
    }
  });

  if (message.aggregatedDeliveryReceipt) {
    const sdkMessage = getSdkMessageObject(message);
    const receipts = await sdkMessage.getDetailedDeliveryReceipts(); // paginated backend query every time

    receipts.forEach((receipt) => {
      if (receipt.status === "read") {
        statuses[MessageStatus.Read] += 1;
      }

      if (receipt.status === "delivered") {
        statuses[MessageStatus.Delivered] += 1;
      }

      if (receipt.status === "failed" || receipt.status === "undelivered") {
        statuses[MessageStatus.Failed] += 1;
      }

      if (receipt.status === "sent" || receipt.status === "queued") {
        statuses[MessageStatus.Sending] += 1;
      }
    });
  }

  return statuses;
}

export const removeParticipant = async (
  conversation: Conversation,
  participant: Participant,
  addNotifications?: (notifications: NotificationsType) => void
): Promise<void> => {
  try {
    await conversation.removeParticipant(participant);
    successNotification({
      message: PARTICIPANT_MESSAGES.REMOVED,
      addNotifications,
    });
  } catch (e) {
    unexpectedErrorNotification(e.message, addNotifications);
    throw e;
  }
};

export const getBlobFile = async (
  media: Media,
  addNotifications?: (notifications: NotificationsType) => void
): Promise<Blob> => {
  try {
    const url = await getFileUrl(media);
    const response = await fetch(url);
    return response.blob();
  } catch (e) {
    unexpectedErrorNotification(e.message, addNotifications);
    throw e;
  }
};

export const getFileUrl = async (media: Media): Promise<string> => {
  return await media.getContentTemporaryUrl().then();
};

export const getMessages = async (
  conversation: Conversation
): Promise<Paginator<Message>> =>
  await conversation.getMessages(CONVERSATION_PAGE_SIZE);
